import { z } from "@reflag/shared/utils/zod";

import {
  CreateFeatureArgsSchema,
  CreateFeatureArgsType,
  LinearCleanupIssueDetails,
  PullRequest,
} from "./featureAPI";
import { FeatureTargetingModes } from "./featureTargetingAPI";
import { UIFilterSchema } from "./filter";
import { EffectiveFlagRuleSchema } from "./flagEvaluation";
import { NotificationProviderSchema } from "./notification";
import {
  ReflagUserHeaderSchema,
  ReflagUserIdSchema,
  SortOrderSchema,
  StageHeaderSchema,
} from "./schemas/shared";
import { SummaryItem } from "./utils/summaryItem";

export const EntitySchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
    avatarUrl: z.string().nullable().optional(),
    isUnknown: z.boolean().optional(),
  })
  .meta({
    id: "entity",
    description: "An entity (company, user, or segment)",
  });

// Schema for targeting rules in summary context - fields are optional since they may not be populated
const SummaryTargetingRuleSchema = z
  .object({
    filter: UIFilterSchema,
    partialRolloutThreshold: z.number().optional(),
    partialRolloutContextAttribute: z.string().optional(),
  })
  .meta({
    id: "summaryTargetingRule",
    description: "A targeting rule in a summary context",
  });

const SummaryEntityItemSchema = z.union([
  z.object({
    slotType: z.literal("entity"),
    type: z.enum(["company", "user", "segment"]),
    entity: EntitySchema,
  }),
  z.object({
    slotType: z.literal("entity"),
    type: z.literal("rule"),
    entity: SummaryTargetingRuleSchema,
  }),
]);

export const SummaryItemSchema = z
  .union([
    SummaryEntityItemSchema,
    z.object({
      slotType: z.literal("summary"),
      type: z.enum(["segment", "company", "user", "rule"]),
      samples: z.array(EntitySchema),
      count: z.number(),
    }),
    z.object({
      slotType: z.literal("more"),
    }),
    z.object({
      slotType: z.literal("none"),
    }),
    z.object({
      slotType: z.literal("everyone"),
    }),
    z.object({
      slotType: z.literal("optIns"),
    }),
    z.object({
      slotType: z.literal("someNoRules"),
    }),
  ])
  .meta({
    id: "summaryItem",
    description: "A summary item representing targeting entities or rules",
  });

export const FlagHeaderCollectionSortBySchema = z.enum([
  "name",
  "key",
  "stage",
  "autoFeedbackSurveysEnabled",
  "createdAt",
  "rolledOutToEveryoneAt",
  "environmentStatus",
  "owner",
  "lastCheck",
  "lastTrack",
  "stale",
  "archivingChecks",
]);

// Legacy values from feature table - accepted but ignored (not exposed in OpenAPI)
const FlagHeaderCollectionSortByLegacySchema = z.enum([
  "latestCheck",
  "latestUsage",
]);

// Input schema that accepts both canonical and legacy values
export const FlagHeaderCollectionSortByInputSchema = z.union([
  FlagHeaderCollectionSortBySchema,
  FlagHeaderCollectionSortByLegacySchema,
]);

// Normalize legacy sortBy values to canonical ones
const flagSortByLegacyMapping: Record<string, FlagHeaderCollectionSortBy> = {
  latestCheck: "lastCheck",
  latestUsage: "lastTrack",
};

export function normalizeFlagSortBy(
  sortBy: z.infer<typeof FlagHeaderCollectionSortByInputSchema>,
): FlagHeaderCollectionSortBy {
  return flagSortByLegacyMapping[sortBy] ?? sortBy;
}

export const FlagStatusSchema = z.enum(["active", "stale", "archived", "all"]);

export const FlagHeaderCollectionQuerySchema = z.object({
  appId: z.string().optional(),
  envId: z.string().optional(),
  sortBy: FlagHeaderCollectionSortByInputSchema.optional().default("createdAt"),
  sortOrder: SortOrderSchema.optional().default("desc"),
  status: FlagStatusSchema.optional().default("active").meta({
    description:
      "Filter flags by status: active, stale, archived, all (default: active)",
  }),
  owner: ReflagUserIdSchema.optional().meta({
    description: "Filter flags by owner ID",
  }),
  view: z.string().optional().meta({
    description: "View ID to filter flags by",
  }),
});

export const FlagKeySchema = z.string().min(1).meta({
  description: "Unique flag key",
  id: "flagKey",
});

export const FlagIdSchema = z.string().min(1).meta({
  description: "Flag ID",
  id: "flagId",
});

// Base schema with fields common to all flag representations
export const FlagBaseSchema = z.object({
  id: FlagIdSchema,
  key: FlagKeySchema,
  name: z.string().min(1).max(255).meta({
    description: "Flag name",
  }),
});
export const FlagHeaderSchema = FlagBaseSchema.extend({
  description: z.string().optional().meta({
    description: "Flag description",
  }),
  stage: StageHeaderSchema.optional(),
  owner: ReflagUserHeaderSchema.optional(),
  archived: z.boolean().meta({
    description: "Whether the flag is archived",
  }),
  stale: z.boolean().meta({
    description: "Whether the flag is stale",
  }),
  permanent: z.boolean().meta({
    description: "Whether the flag is permanent",
  }),
  createdAt: z.string().optional().meta({
    description: "Timestamp when the flag was created",
  }),
  rolledOutToEveryoneAt: z.string().optional().meta({
    description: "Timestamp when the flag was rolled out to everyone",
  }),
  codeRefsCleanedUp: z.boolean().meta({
    description: "Whether code references for this flag have been cleaned up",
  }),
  codeRefsMarkedCleanUserName: z.string().optional().meta({
    description: "Name of the user who marked code references as cleaned up",
  }),
  codeRefsMarkedCleanAt: z.string().optional().meta({
    description: "Timestamp when code references were marked as cleaned up",
  }),
  lastCheckAt: z.string().optional().meta({
    description: "Timestamp when the flag was last checked",
  }),
  noRecentChecks: z.boolean().meta({
    description: "Whether the flag has no recent access checks",
  }),
  lastTrackAt: z.string().optional().meta({
    description: "Timestamp when the flag was last tracked",
  }),
}).meta({
  id: "flagHeader",
  description: "Basic flag information",
});

export const FlagSchema = FlagHeaderSchema.extend({
  automatedSurveysEnabled: z.boolean().optional().meta({
    description: "Whether automated surveys are enabled for this flag",
  }),
  parentFlagId: z.string().optional().meta({
    description: "Parent flag ID",
  }),
  feedbackCount: z.number().optional().meta({
    description: "Feedback count for this flag",
  }),
}).meta({
  id: "flag",
  description: "Flag",
});

export const FlagHeaderCollectionSchema = z
  .object({
    data: z.array(FlagHeaderSchema).meta({
      description: "Page of the collection of flags",
    }),
    totalCount: z.number().int().meta({
      description: "Total number of flags in collection",
    }),
    pageSize: z.number().int().meta({
      description: "Page size",
    }),
    pageIndex: z.number().int().meta({
      description: "Page index",
    }),
    sortBy: FlagHeaderCollectionSortBySchema.meta({
      description: "Sort by",
    }),
    sortOrder: SortOrderSchema.meta({
      description: "Sort order",
    }),
  })
  .meta({
    id: "flagHeaderCollection",
    description: "Collection response containing flags",
  });

export type FlagHeaderDTO = z.output<typeof FlagHeaderSchema> & {
  // we're adding these internally only for now.
  // it's not available in the public Management API yet.
  featureId: string;
  cleanupPullRequest?: PullRequest;
  linearCleanupIssue?: {
    url: string;
    details: LinearCleanupIssueDetails | null;
  };
  feedbackCount: number | undefined;
  automatedSurveysEnabled: boolean | undefined;
  parentFlagId: string | undefined;
  targetingSummary: SummaryItem[] | undefined;
};
export type FlagDTO = z.output<typeof FlagSchema>;

export const FlagTargetingVersionSchema = z.number().int().meta({
  description: "Flag targeting version",
  id: "flagVersion",
});

export const FlagStateVersionSchema = z.number().int().nonnegative().meta({
  description:
    "Environment flag-state version containing the completed mutation",
  id: "flagStateVersion",
});

export const AppFlagStateVersionsSchema = z
  .record(z.string(), FlagStateVersionSchema)
  .meta({
    description: "Current flag-state version keyed by environment ID",
    id: "appFlagStateVersions",
  });

const EntityIdSchema = z
  .string()
  .min(1)
  .max(128)
  .refine((id) => !id.includes("\0"), "ID must not contain null bytes");

export const CompanyIdSchema = EntityIdSchema.meta({
  description: "Company ID within your application",
  id: "companyId",
});

export const UserIdSchema = EntityIdSchema.meta({
  description: "User ID within your application",
  id: "userId",
});

export const FlagValueTargetingSchema = z
  .object({
    companyIds: z.array(CompanyIdSchema).meta({
      description: "Companies that were explicitly given the value",
    }),
    userIds: z.array(UserIdSchema).meta({
      description: "Users that were explicitly given the value",
    }),
  })
  .meta({
    id: "flagValueTargeting",
    description: "Flag targeting value and its audience",
  });

export const FlagValueSchema = z.literal("true").meta({
  description:
    "The saved explicit value for the audience; does not imply that targeting is active.",
  id: "flagValue",
});

export const FlagTargetingSchema = z
  .object({
    flagKey: FlagKeySchema,
    version: FlagTargetingVersionSchema,
    targetingMode: z.enum(FeatureTargetingModes).meta({
      description:
        "Authored targeting mode. Saved specific targets are inactive when this is none and are not required when it is everyone.",
    }),
    effectiveRules: z.array(EffectiveFlagRuleSchema).meta({
      description:
        "Compiled runtime enable rules, including active opt-ins, with segment and flag references resolved. Rules are evaluated independently (OR), not as one combined rule. Empty for No one, archived flags, or a configuration with no enable rules. Uses the same authored snapshot as version; does not guarantee SDK propagation or reproduce a live SDK context.",
    }),
    updatedAt: z.iso.datetime().meta({
      description: "Last time the targeting was updated",
    }),
    specificTargets: z.record(FlagValueSchema, FlagValueTargetingSchema).meta({
      description:
        "Saved explicit assignments, preserved even when inactive. Use targetingMode and effectiveRules to interpret effective targeting, not list membership alone.",
    }),
  })
  .meta({
    id: "flagTargeting",
    description: "Flag targeting information and its audience",
  });

export const FlagTargetingCollectionSchema = z
  .object({
    data: FlagTargetingSchema.array().meta({
      description: "Collection of flags' targeting information",
    }),
    flagStateVersion: FlagStateVersionSchema,
  })
  .meta({
    id: "flagTargetingCollection",
    description: "Collection response containing flags' targeting information",
  });

export const UpdateFlagSpecificTargetsSchema = z
  .object({
    flagKey: FlagKeySchema,
    value: z.union([z.literal("true"), z.literal(true), z.null()]).meta({
      description:
        "The value of the flag for the given audience (null means to remove the flag from the audience).",
    }),
    companyId: CompanyIdSchema.optional(),
    userId: UserIdSchema.optional(),
  })
  .refine(
    ({ companyId, userId }) => {
      return !companyId !== !userId;
    },
    {
      message: "Either companyId or userId must be provided, but not both.",
    },
  )
  .meta({
    id: "updateFlagSpecificTargets",
    description:
      "Update the explicit value of the flag for a given audience (null means to remove the flag from the audience).",
  });

export const BulkUpdateFlagSpecificTargetsSchema = z
  .object({
    updates: UpdateFlagSpecificTargetsSchema.array().min(1).meta({
      description: "The list of updates to make to the flags' targeting",
    }),
    notifications: NotificationProviderSchema.array().optional().meta({
      description:
        "Destination list for notifications about the change. Use [] to disable notifications. Omit to use configured defaults.",
    }),
    changeDescription: z.string().optional().meta({
      description:
        "The description of the change recorded in the change history",
    }),
  })
  .meta({
    id: "bulkUpdateFlagSpecificTargetsSchema",
    description:
      "Update the explicit value of multiple flags for a given audience",
  });

export type FlagHeaderCollectionSortBy = z.infer<
  typeof FlagHeaderCollectionSortBySchema
>;
export type FlagHeaderCollectionColumn =
  | keyof FlagHeaderDTO
  | "archivingChecks"
  | "cleanup";
export type FlagHeaderCollectionQuery = z.output<
  typeof FlagHeaderCollectionQuerySchema
>;
export type FlagHeaderCollectionQueryInput = z.input<
  typeof FlagHeaderCollectionQuerySchema
>;

// FlagHeaderDTO has additional fields that are not documented in the public Management API yet.
export type FlagHeaderCollection = z.output<
  typeof FlagHeaderCollectionSchema
> & { data: FlagHeaderDTO[] };

// Schema for query parameters when fetching flags for a user or company
export const EntityFlagsQuerySchema = z
  .object({
    envId: z.string().min(1).meta({
      description: "Environment ID to evaluate targeting for",
    }),
  })
  .meta({
    id: "entityFlagsQuery",
    description: "Query parameters for fetching flags for an entity",
  });

// Schema for a single flag with its evaluation result for a specific entity
export const EntityFlagSchema = FlagBaseSchema.extend({
  createdAt: z.string().meta({
    description: "Timestamp when the flag was created",
  }),
  // Targeting evaluation
  value: z.boolean().meta({
    description:
      "Evaluated enabled state using current runtime rules and stored entity attributes, not the last SDK result or a default value. Company checks supply only company context. User checks supply user context and also check each stored company membership; no other/request context is supplied. Stored attributes and context may differ from a live SDK request.",
  }),
  specificTargetValue: z.boolean().nullable().meta({
    description:
      "Saved explicit assignment, or null if absent. May be true while value is false when targeting is No one. This is configuration metadata, not an effective-access override.",
  }),
  // Entity-specific metrics
  firstExposureAt: z.string().nullable().meta({
    description: "First time the entity was exposed to this flag",
  }),
  lastExposureAt: z.string().nullable().meta({
    description: "Last time the entity was exposed to this flag",
  }),
  lastCheckAt: z.string().nullable().meta({
    description: "Last time the flag was checked for this entity",
  }),
  exposureCount: z.number().int().meta({
    description: "Number of times the entity was exposed to this flag",
  }),
  firstTrackAt: z.string().nullable().meta({
    description: "First time a track event was recorded for this flag",
  }),
  lastTrackAt: z.string().nullable().meta({
    description: "Last time a track event was recorded for this flag",
  }),
  trackCount: z.number().int().meta({
    description: "Number of track events for this flag",
  }),
}).meta({
  id: "entityFlag",
  description: "Flag information with enabled status for an entity",
});

// Response schema for entity flags
export const EntityFlagsResponseSchema = z
  .object({
    data: z.array(EntityFlagSchema).meta({
      description: "List of flags with their enabled status",
    }),
    totalCount: z.number().int().meta({
      description: "Total number of flags",
    }),
    pageSize: z.number().int().meta({
      description: "Page size",
    }),
    pageIndex: z.number().int().meta({
      description: "Page index",
    }),
  })
  .meta({
    id: "entityFlagsResponse",
    description: "Response containing flags for an entity",
  });

export type EntityFlagsQuery = z.output<typeof EntityFlagsQuerySchema>;
export type EntityFlagDTO = z.output<typeof EntityFlagSchema>;
export const UpdateEntityFlagsResponseSchema = EntityFlagsResponseSchema.extend(
  {
    flagStateVersion: FlagStateVersionSchema,
  },
).meta({
  id: "updateEntityFlagsResponse",
  description:
    "Updated entity flags and the environment flag-state version containing the change",
});

export type EntityFlagsResponse = z.output<typeof EntityFlagsResponseSchema>;
export type UpdateEntityFlagsResponse = z.output<
  typeof UpdateEntityFlagsResponseSchema
>;

// Schema for updating specific targeting for a single flag
export const EntityFlagUpdateSchema = z
  .object({
    flagKey: FlagKeySchema,
    specificTargetValue: z.union([z.literal(true), z.null()]).meta({
      description:
        "Set to true to add a specific-targeting override, or null to remove it",
    }),
  })
  .meta({
    id: "entityFlagUpdate",
    description: "Update for a single flag's explicit targeting override",
  });

// Schema for bulk updating specific targeting for an entity
export const UpdateEntityFlagsBodySchema = z
  .object({
    updates: z.array(EntityFlagUpdateSchema).min(1).meta({
      description: "List of flag updates to apply",
    }),
    changeDescription: z.string().optional().meta({
      description: "Description of the change for audit history",
    }),
    notifications: NotificationProviderSchema.array().optional().meta({
      description:
        "Destination list for notifications about the change. Use [] to disable notifications. Omit to use configured defaults.",
    }),
  })
  .meta({
    id: "updateEntityFlagsBody",
    description: "Request body for updating flags for an entity",
  });

export type EntityFlagUpdate = z.output<typeof EntityFlagUpdateSchema>;
export type UpdateEntityFlagsBody = z.output<
  typeof UpdateEntityFlagsBodySchema
>;

export const PatchFlagOpenApiRequestSchema = z
  .object({
    name: CreateFeatureArgsSchema.shape.name,
    description: CreateFeatureArgsSchema.shape.description,
    ownerUserId: CreateFeatureArgsSchema.shape.ownerUserId,
    // `.partial()` suppresses defaults at parse time, but inheriting
    // `CreateFeatureArgsSchema.shape.isPermanent` leaks `default: false`
    // into the generated PATCH schema.
    permanent: z.boolean().optional(),
    secret: CreateFeatureArgsSchema.shape.secret,
    isArchived: z.boolean(),
    stageId: CreateFeatureArgsSchema.shape.stageId,
  })
  .strict()
  .partial();

export type PatchFlagOpenApiRequestType = z.input<
  typeof PatchFlagOpenApiRequestSchema
>;

export interface FlagAPI {
  "/apps/:appId/flags": {
    GET: {
      response: FlagHeaderCollection;
      params: FlagHeaderCollectionQuery;
    };
    POST: {
      body: CreateFeatureArgsType;
      response: {
        flag: FlagDTO;
        flagStateVersions: z.output<typeof AppFlagStateVersionsSchema>;
      };
      params: {
        appId: string;
      };
    };
  };
  "/apps/:appId/flags/:flagId": {
    PATCH: {
      body: PatchFlagOpenApiRequestType;
      response: {
        flag: FlagDTO;
        flagStateVersions: z.output<typeof AppFlagStateVersionsSchema>;
      };
      params: {
        appId: string;
        flagId: string;
      };
    };
  };
  "/apps/:appId/flags/:flagKey/targeting/:envId": {
    GET: {
      response: z.output<typeof FlagTargetingSchema>;
      params: {
        appId: string;
        flagKey: string;
        envId: string;
      };
    };
  };
  /**
   * @deprecated Use /apps/:appId/envs/:envId/companies/:companyId/flags and
   * /apps/:appId/envs/:envId/users/:userId/flags instead.
   */
  "/apps/:appId/flags/specific-targets/:envId": {
    PATCH: {
      body: z.input<typeof BulkUpdateFlagSpecificTargetsSchema>;
      response: z.output<typeof FlagTargetingCollectionSchema>;
      params: {
        appId: string;
        envId: string;
      };
    };
  };
}
